import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users, GraduationCap, FileText, BookOpen, FolderOpen, UserCheck,
  Clock, CreditCard, Brain, Search, ArrowLeft, Download, ChevronRight,
  Mail, Calendar, Target, BarChart3, Shield, Activity, Eye, TrendingUp,
  Globe, KeyRound, CheckCircle2, XCircle, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { DiagnosticLeadsTab } from '../components/DiagnosticLeadsTab';
import { CommonAppRefreshTab } from '../components/CommonAppRefreshTab';

function StatCard({ icon: Icon, label, value, color = "text-[#0F392B]" }) {
  return (
    <div className="bg-white rounded-xl border border-[#E2E2E2] p-5 hover:shadow-md transition-shadow" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
          <p className={`text-3xl font-semibold mt-1 ${color}`} style={{ fontFamily: "'Playfair Display', serif" }}>{value}</p>
        </div>
        <div className="w-10 h-10 rounded-lg bg-[#0F392B]/5 flex items-center justify-center">
          <Icon className="h-5 w-5 text-[#0F392B]" strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
}

function GrantAccessPanel({ onChange }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const submit = async (revoke) => {
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Enter an email');
      return;
    }
    setLoading(true);
    try {
      const res = await api.grantUserAccess(trimmed, revoke);
      setLastResult({ action: res.action, email: res.email });
      toast.success(res.message);
      setEmail('');
      onChange?.();
    } catch (err) {
      toast.error(err?.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const seedDemos = async () => {
    if (!window.confirm('This will create or refresh 5 elite demo accounts (Aisha/Marcus/Sophie/James/Mia). Existing activities & essays for these accounts will be replaced. Continue?')) return;
    setSeeding(true);
    try {
      const res = await api.seedDemoAccounts();
      const s = res.summary;
      toast.success(`Demo seed complete — ${s.created} created, ${s.updated} updated. Password: ${s.password}`);
      onChange?.();
    } catch (err) {
      toast.error(err?.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Card className="border-[#E2E2E2] mb-6" data-testid="admin-grant-access-panel">
      <div className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
            <KeyRound className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.75} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>Grant Portal Access</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Bypass the $99/yr paywall for a specific user. They must have a registered account.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit(false)}
            placeholder="user@example.com"
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-[#E2E2E2] bg-white focus:outline-none focus:ring-2 focus:ring-[#0F392B] focus:border-transparent"
            data-testid="admin-grant-email-input"
            disabled={loading}
          />
          <Button
            onClick={() => submit(false)}
            disabled={loading || !email.trim()}
            className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-xs h-9"
            data-testid="admin-grant-access-btn"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
            Grant Access
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={loading || !email.trim()}
            variant="outline"
            className="text-xs h-9 border-red-200 text-red-600 hover:bg-red-50"
            data-testid="admin-revoke-access-btn"
          >
            <XCircle className="h-3.5 w-3.5 mr-1" /> Revoke
          </Button>
        </div>
        {lastResult && (
          <p className="text-[11px] text-muted-foreground mt-3">
            Last: <span className={`font-medium ${lastResult.action === 'granted' ? 'text-emerald-600' : 'text-red-600'}`}>{lastResult.action}</span> for <span className="font-mono">{lastResult.email}</span>
          </p>
        )}

        {/* Demo Accounts Seeder */}
        <div className="mt-5 pt-5 border-t border-[#E2E2E2]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-[#0F392B]">Seed Elite Demo Accounts</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Creates 5 showcase candidates (Harvard / Princeton / Yale / Stanford / UF Honors) with full profiles, 10 activities, and essays each. Use for live pitch demos.</p>
            </div>
            <Button
              onClick={seedDemos}
              disabled={seeding}
              variant="outline"
              className="text-xs h-9 border-[#D4AF37]/40 text-[#0F392B] hover:bg-[#D4AF37]/10 whitespace-nowrap"
              data-testid="admin-seed-demos-btn"
            >
              {seeding ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <GraduationCap className="h-3.5 w-3.5 mr-1" />}
              Seed Demo Accounts
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function UserRow({ user, onSelect }) {
  return (
    <tr
      className="border-b border-[#E2E2E2] hover:bg-[#F9F9F7] cursor-pointer transition-colors"
      onClick={() => onSelect(user.id)}
      data-testid={`admin-user-row-${user.id}`}
    >
      <td className="py-4 px-4">
        <div>
          <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
          <p className="text-xs text-muted-foreground">{user.email}</p>
        </div>
      </td>
      <td className="py-4 px-4">
        <Badge variant="outline" className="text-xs">{user.role}</Badge>
      </td>
      <td className="py-4 px-4 text-sm">{user.gpa || '—'}</td>
      <td className="py-4 px-4 text-sm">{user.test_score || '—'}</td>
      <td className="py-4 px-4 text-sm">{user.intended_major || '—'}</td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span title="Colleges">{user.college_count} colleges</span>
          <span title="Essays">{user.essay_count} essays</span>
          <span title="Activities">{user.activity_count} activities</span>
        </div>
      </td>
      <td className="py-4 px-4 text-xs text-muted-foreground">
        {new Date(user.created_at).toLocaleDateString()}
      </td>
      <td className="py-4 px-4">
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </td>
    </tr>
  );
}

function UserDetail({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getAdminUserDetail(userId).then(d => {
      setData(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-muted-foreground">User not found</div>;

  const { user, colleges, essays, activities, documents, recommenders, timeline, match_scores, ai_feedbacks, payments } = data;

  return (
    <div data-testid="admin-user-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="admin-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to all users
      </button>

      {/* User Header */}
      <div className="bg-[#0F392B] rounded-2xl p-8 mb-8 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
              {user.first_name} {user.last_name}
            </h2>
            <div className="flex items-center gap-4 mt-2 text-white/70 text-sm">
              <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{user.email}</span>
              <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />Joined {new Date(user.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <Badge className="bg-white/10 text-white border-white/20">{user.role}</Badge>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-xs text-white/60">GPA</p>
            <p className="text-lg font-medium">{user.gpa || '—'}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-xs text-white/60">SAT/ACT</p>
            <p className="text-lg font-medium">{user.test_score || '—'}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-xs text-white/60">Major</p>
            <p className="text-lg font-medium">{user.intended_major || '—'}</p>
          </div>
        </div>
      </div>

      {/* Data Sections */}
      <div className="space-y-6">
        {/* Colleges */}
        <DataSection title="Colleges" icon={GraduationCap} count={colleges.length} data-testid="admin-colleges-section">
          {colleges.map(c => (
            <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F9F9F7]">
              <div>
                <p className="text-sm font-medium">{c.name}</p>
                <p className="text-xs text-muted-foreground">Deadline: {c.deadline || 'Not set'}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-xs ${c.college_type === 'reach' ? 'border-red-300 text-red-700' : c.college_type === 'safety' ? 'border-green-300 text-green-700' : 'border-blue-300 text-blue-700'}`}>
                  {c.college_type}
                </Badge>
                <Badge variant="outline" className="text-xs">{c.status}</Badge>
              </div>
            </div>
          ))}
        </DataSection>

        {/* Essays */}
        <DataSection title="Essays" icon={FileText} count={essays.length} data-testid="admin-essays-section">
          {essays.map(e => (
            <div key={e.id} className="p-3 rounded-lg bg-[#F9F9F7]">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium">{e.title}</p>
                <Badge variant="outline" className="text-xs">{e.status}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">School: {e.school} | Words: ~{e.content ? e.content.split(' ').length : 0} | v{e.version_number}</p>
              {e.content && <p className="text-xs text-muted-foreground mt-2 line-clamp-2 italic">{e.content.substring(0, 200)}...</p>}
            </div>
          ))}
        </DataSection>

        {/* Activities */}
        <DataSection title="Activities" icon={Target} count={activities.length} data-testid="admin-activities-section">
          {activities.map(a => (
            <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F9F9F7]">
              <div>
                <p className="text-sm font-medium">{a.name}</p>
                <p className="text-xs text-muted-foreground">{a.category} | {a.hours_per_week}h/wk, {a.weeks_per_year}wk/yr</p>
              </div>
              <Badge variant="outline" className="text-xs">{a.role}</Badge>
            </div>
          ))}
        </DataSection>

        {/* Match Scores */}
        <DataSection title="Match Scores" icon={BarChart3} count={match_scores.length} data-testid="admin-matches-section">
          {match_scores.map(m => (
            <div key={m.id} className="p-3 rounded-lg bg-[#F9F9F7]">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium">{m.college_name}</p>
                <span className="text-lg font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>{m.score}/100</span>
              </div>
              <p className="text-xs text-muted-foreground">{m.recommendation}</p>
            </div>
          ))}
        </DataSection>

        {/* AI Feedbacks */}
        <DataSection title="AI Feedbacks" icon={Brain} count={ai_feedbacks.length} data-testid="admin-feedbacks-section">
          {ai_feedbacks.map(f => (
            <div key={f.id} className="p-3 rounded-lg bg-[#F9F9F7]">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-xs">{f.feedback_type}</Badge>
                <span className="text-xs text-muted-foreground">{new Date(f.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">{f.feedback_text?.substring(0, 200)}...</p>
            </div>
          ))}
        </DataSection>

        {/* Documents */}
        <DataSection title="Documents" icon={FolderOpen} count={documents.length} data-testid="admin-docs-section">
          {documents.map(d => (
            <div key={d.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F9F9F7]">
              <p className="text-sm font-medium">{d.name}</p>
              <Badge variant="outline" className="text-xs">{d.document_type}</Badge>
            </div>
          ))}
        </DataSection>

        {/* Recommenders */}
        <DataSection title="Recommenders" icon={UserCheck} count={recommenders.length} data-testid="admin-recs-section">
          {recommenders.map(r => (
            <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F9F9F7]">
              <div>
                <p className="text-sm font-medium">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.email}</p>
              </div>
              <Badge variant="outline" className="text-xs">{r.status}</Badge>
            </div>
          ))}
        </DataSection>

        {/* Payments */}
        <DataSection title="Payments" icon={CreditCard} count={payments.length} data-testid="admin-payments-section">
          {payments.map(p => (
            <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F9F9F7]">
              <div>
                <p className="text-sm font-medium">${p.amount} — {p.package_id}</p>
                <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</p>
              </div>
              <Badge variant="outline" className={`text-xs ${p.payment_status === 'paid' ? 'border-green-300 text-green-700' : 'border-yellow-300 text-yellow-700'}`}>
                {p.payment_status}
              </Badge>
            </div>
          ))}
        </DataSection>
      </div>
    </div>
  );
}


function AnalyticsTab() {
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getAnalyticsSummary(days).then(d => {
      setAnalytics(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading analytics...</div>;
  if (!analytics) return <div className="text-center py-12 text-muted-foreground">No analytics data yet</div>;

  const maxDailyViews = Math.max(...(analytics.daily || []).map(d => d.views), 1);

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Period Selector */}
      <div className="flex items-center gap-2">
        {[7, 14, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${days === d ? 'bg-[#0F392B] text-white' : 'bg-white border border-[#E2E2E2] text-muted-foreground hover:bg-[#F9F9F7]'}`}
            data-testid={`analytics-period-${d}`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Total Views" value={analytics.total_views} />
        <StatCard icon={Users} label="Unique Visitors" value={analytics.unique_visitors} />
        <StatCard icon={TrendingUp} label="Avg/Day" value={analytics.daily.length > 0 ? Math.round(analytics.total_views / analytics.daily.length) : 0} />
        <StatCard icon={Globe} label="Top Referrers" value={analytics.referrers.length} />
      </div>

      {/* Daily Traffic Chart */}
      <Card className="border-[#E2E2E2] p-5">
        <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
          <Activity className="h-4 w-4 text-[#0F392B]" /> Daily Page Views
        </h3>
        {analytics.daily.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No traffic data yet. Views will appear as visitors browse your site.</p>
        ) : (
          <div className="space-y-1.5">
            {analytics.daily.slice(-14).map((d, i) => {
              const uniqueDay = analytics.daily_unique.find(u => u.date === d.date);
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`analytics-day-${i}`}>
                  <span className="text-[10px] text-muted-foreground w-20 flex-shrink-0">{new Date(d.date + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  <div className="flex-1 bg-[#F9F9F7] rounded-full h-5 overflow-hidden relative">
                    <div
                      className="h-full bg-[#0F392B] rounded-full transition-all duration-500"
                      style={{ width: `${Math.max((d.views / maxDailyViews) * 100, 2)}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium w-16 text-right">{d.views} views</span>
                  <span className="text-[10px] text-muted-foreground w-16 text-right">{uniqueDay?.unique_visitors || 0} unique</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Pages + Referrers side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Pages */}
        <Card className="border-[#E2E2E2] p-5">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#0F392B]" /> Top Pages
          </h3>
          {analytics.pages.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No page data yet</p>
          ) : (
            <div className="space-y-2">
              {analytics.pages.slice(0, 12).map((p, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#E2E2E2] last:border-0" data-testid={`analytics-page-${i}`}>
                  <span className="text-xs font-mono text-muted-foreground truncate max-w-[200px]">{p.path}</span>
                  <Badge variant="outline" className="text-xs">{p.views}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Top Referrers */}
        <Card className="border-[#E2E2E2] p-5">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <Globe className="h-4 w-4 text-[#0F392B]" /> Top Referrers
          </h3>
          {analytics.referrers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No referrer data yet</p>
          ) : (
            <div className="space-y-2">
              {analytics.referrers.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#E2E2E2] last:border-0">
                  <span className="text-xs text-muted-foreground truncate max-w-[200px]">{r.referrer}</span>
                  <Badge variant="outline" className="text-xs">{r.count}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Conversion Funnel */}
      {analytics.funnel && (
        <Card className="border-[#E2E2E2] p-5" data-testid="analytics-funnel">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <Target className="h-4 w-4 text-[#0F392B]" /> Conversion Funnel
          </h3>
          <div className="space-y-2">
            {[
              { label: 'Page Views', value: analytics.funnel.page_views, color: 'bg-[#0F392B]' },
              { label: 'Quiz Completions', value: analytics.funnel.quiz_completions, color: 'bg-[#0F392B]/80' },
              { label: 'CTA Clicks', value: analytics.funnel.cta_clicks, color: 'bg-[#D4AF37]' },
              { label: 'Lead Captures', value: analytics.funnel.lead_captures, color: 'bg-[#D4AF37]/80' },
              { label: 'Sign Ups', value: analytics.funnel.sign_ups, color: 'bg-emerald-600' },
              { label: 'Checkouts Started', value: analytics.funnel.checkouts_started, color: 'bg-emerald-600/80' },
              { label: 'Purchases', value: analytics.funnel.purchases, color: 'bg-emerald-500' },
            ].map((step, i) => {
              const maxVal = analytics.funnel.page_views || 1;
              const pct = Math.max((step.value / maxVal) * 100, 1);
              const rate = i > 0 && analytics.funnel.page_views > 0 ? ((step.value / analytics.funnel.page_views) * 100).toFixed(1) : null;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`funnel-step-${i}`}>
                  <span className="text-[11px] text-muted-foreground w-32 flex-shrink-0">{step.label}</span>
                  <div className="flex-1 bg-[#F9F9F7] rounded-full h-6 overflow-hidden">
                    <div className={`h-full ${step.color} rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                      style={{ width: `${pct}%`, minWidth: step.value > 0 ? '28px' : '4px' }}>
                      {step.value > 0 && <span className="text-[10px] text-white font-medium">{step.value}</span>}
                    </div>
                  </div>
                  {rate && <span className="text-[10px] text-muted-foreground w-12 text-right">{rate}%</span>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Events + UTM side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Events Breakdown */}
        <Card className="border-[#E2E2E2] p-5" data-testid="analytics-events">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <Activity className="h-4 w-4 text-[#0F392B]" /> Events Tracked
          </h3>
          {(!analytics.events || analytics.events.length === 0) ? (
            <p className="text-sm text-muted-foreground py-2">No events yet. Events fire as users interact with the site.</p>
          ) : (
            <div className="space-y-2">
              {analytics.events.map((e, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#E2E2E2] last:border-0" data-testid={`event-row-${i}`}>
                  <span className="text-xs font-mono text-muted-foreground">{e.event}</span>
                  <Badge variant="outline" className="text-xs">{e.count}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* UTM Campaign Tracking */}
        <Card className="border-[#E2E2E2] p-5" data-testid="analytics-utm">
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
            <Target className="h-4 w-4 text-[#0F392B]" /> UTM Campaigns
          </h3>
          {(!analytics.utm || analytics.utm.total_utm_views === 0) ? (
            <p className="text-sm text-muted-foreground py-2">No UTM-tagged traffic yet. Share links with ?utm_source=... to track campaigns.</p>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Sources</p>
                {analytics.utm.sources.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-1 border-b border-[#E2E2E2] last:border-0">
                    <span className="text-xs text-muted-foreground">{s.source}</span>
                    <Badge variant="outline" className="text-xs">{s.views}</Badge>
                  </div>
                ))}
              </div>
              {analytics.utm.campaigns.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Campaigns</p>
                  {analytics.utm.campaigns.map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b border-[#E2E2E2] last:border-0">
                      <span className="text-xs text-muted-foreground">{c.campaign}</span>
                      <Badge variant="outline" className="text-xs">{c.views}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function DataSection({ title, icon: Icon, count, children, ...props }) {
  const [open, setOpen] = useState(count > 0);
  return (
    <Card className="border-[#E2E2E2]" {...props}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 hover:bg-[#F9F9F7] transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-[#0F392B]" strokeWidth={1.5} />
          <span className="text-sm font-medium">{title}</span>
          <Badge variant="outline" className="text-xs">{count}</Badge>
        </div>
        <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <CardContent className="pt-0 pb-5 px-5">
          {count === 0 ? (
            <p className="text-sm text-muted-foreground italic py-2">No {title.toLowerCase()} yet</p>
          ) : (
            <div className="space-y-2">{children}</div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('analytics');

  useEffect(() => {
    Promise.all([
      api.getAdminStats(),
      api.getAdminUsers(),
    ]).then(([statsData, usersData]) => {
      setStats(statsData);
      setUsers(usersData);
      setLoading(false);
    }).catch(err => {
      setError(err.message || 'Admin access denied');
      setLoading(false);
    });
  }, []);

  const handleExport = () => {
    api.exportAdminUsers().then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    });
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (u.first_name + ' ' + u.last_name).toLowerCase().includes(q)
      || u.email.toLowerCase().includes(q)
      || (u.intended_major || '').toLowerCase().includes(q);
  });

  const refreshUsers = () => {
    api.getAdminUsers().then(setUsers).catch(() => {});
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-[#0F392B] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="text-center space-y-3">
          <Shield className="h-12 w-12 text-red-400 mx-auto" />
          <h2 className="text-xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Access Denied</h2>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="admin-page">
      <SEO title="Admin Dashboard" description="Admin panel for Ivy League Consulting" noindex />

      {/* Header */}
      <header className="bg-white border-b border-[#E2E2E2] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Admin Dashboard</h1>
            <p className="text-xs text-muted-foreground">Logged in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={handleExport} className="text-xs" data-testid="admin-export-btn">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
            <a href="/dashboard">
              <Button variant="ghost" size="sm" className="text-xs">Back to App</Button>
            </a>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto flex items-center gap-1 mt-4">
          {[
            { key: 'analytics', label: 'Website Analytics', icon: Activity },
            { key: 'users', label: 'Students', icon: Users },
            { key: 'diagnostic', label: 'Diagnostic Leads', icon: Target },
            { key: 'common-app', label: 'Common App Grid', icon: RefreshCw },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg transition-colors ${
                activeTab === tab.key
                  ? 'bg-[#0F392B] text-white'
                  : 'text-muted-foreground hover:bg-[#F9F9F7]'
              }`}
              data-testid={`admin-tab-${tab.key}`}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'analytics' && <AnalyticsTab />}

        {activeTab === 'diagnostic' && <DiagnosticLeadsTab />}

        {activeTab === 'common-app' && <CommonAppRefreshTab />}

        {activeTab === 'users' && (
          <>
        {selectedUserId ? (
          <UserDetail userId={selectedUserId} onBack={() => setSelectedUserId(null)} />
        ) : (
          <>
            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
                <StatCard icon={Users} label="Users" value={stats.total_users} />
                <StatCard icon={GraduationCap} label="Colleges" value={stats.total_colleges} />
                <StatCard icon={FileText} label="Essays" value={stats.total_essays} />
                <StatCard icon={BookOpen} label="Activities" value={stats.total_activities} />
                <StatCard icon={Brain} label="AI Feedbacks" value={stats.total_ai_feedbacks} />
                <StatCard icon={CreditCard} label="Payments" value={stats.total_payments} />
              </div>
            )}

            {/* Search + Grant Access */}
            <GrantAccessPanel onChange={refreshUsers} />
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search users by name, email, or major..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border border-[#E2E2E2] bg-white focus:outline-none focus:ring-2 focus:ring-[#0F392B] focus:border-transparent"
                  data-testid="admin-search-input"
                />
              </div>
            </div>

            {/* Users Table */}
            <Card className="border-[#E2E2E2] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-users-table">
                  <thead>
                    <tr className="bg-[#F9F9F7] border-b border-[#E2E2E2]">
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Student</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Role</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">GPA</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">SAT</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Major</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Data</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Joined</th>
                      <th className="py-3 px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <UserRow key={u.id} user={u} onSelect={setSelectedUserId} />
                    ))}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-8">No users found</p>
                )}
              </div>
            </Card>
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
}
